// Lista przebiegów sztuki z bramy (`GET /brama/urzadzenia/{sn}/przebiegi`, ST-1): filtry, strony i kolumny
// podsumowania liczy serwer - APP tylko wyświetla.
import { useEffect, useState } from "react";
import { plik, zapytanie } from "../api/zaplecze";
import type { AfterIn, AfterReply, OKReply, PoleParagonu, RunGalleryIn, RunGalleryReply, RunReply, RunsReply, RunTrialIn, StatusPrzebiegu,
  UkladParagonu } from "../gen/kontrakt";

export interface FiltrPrzebiegow {
  od?: number; // unix s; brak = bez granicy (lista domyślnie pokazuje wszystkie przebiegi)
  do?: number;
  program: string; // "" = każdy
  status: StatusPrzebiegu | "";
  proby?: boolean; // same próby (zwinięta lista „Próby”), inaczej bez nich
}

export function pobierzPrzebiegi(sn: string, f: FiltrPrzebiegow, strona: number): Promise<RunsReply> {
  const q = new URLSearchParams({ strona: String(strona) });
  if (f.od != null) q.set("od", String(Math.floor(f.od)));
  if (f.do != null) q.set("do", String(Math.ceil(f.do)));
  if (f.program) q.set("program", f.program);
  if (f.status) q.set("status", f.status);
  if (f.proby) q.set("proby", "1");
  return zapytanie<RunsReply>("GET", `/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi?${q}`);
}

/** Ostatni zamknięty przebieg z listy (pierwszy z końcem na stronie 0) - „Start” / „Koniec” na karcie procesu po
 *  zakończeniu, gdy zegar procesu w firmware stoi. Pobiera tylko `poProcesie`: bez otwartego przebiegu i ze
 *  sterownikiem, który nadal trzyma czas procesu (restart zeruje czasy - wtedy nie ma czego pokazywać); null = nie
 *  ten stan, jeszcze nie pobrany albo brak zapisanych przebiegów. */
export function useOstatniPrzebieg(sn: string, poProcesie: boolean) {
  const [ostatni, ustawOstatni] = useState<{ sn: string; start: number; koniec: number } | null>(null);
  useEffect(() => {
    if (!poProcesie) return;
    let zywy = true;
    pobierzPrzebiegi(sn, { program: "", status: "" }, 0)
      .then((o) => {
        const r = o.przebiegi.find((p) => p.koniec != null);
        if (zywy) ustawOstatni(r?.koniec != null ? { sn, start: r.start, koniec: r.koniec } : null);
      })
      .catch(() => zywy && ustawOstatni(null));
    return () => { zywy = false; };
  }, [sn, poProcesie]);
  return poProcesie && ostatni?.sn === sn ? ostatni : null;
}

/** Po wędzeniu przy przebiegu (dojrzewanie, „spożyć do”) - liczy worker. */
export const poWedzeniu = (sn: string, id: number) =>
  zapytanie<AfterReply>("GET", `/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}/po`);

export const zmienPoWedzeniu = (sn: string, id: number, z: Partial<AfterIn>) =>
  zapytanie<AfterReply>("POST", `/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}/po`, z);

/** Etykieta wyrobu 100 × 70 mm (składa worker): PDF do druku albo PNG do udostępnienia. */
export function etykieta(sn: string, id: number, prezentowa: boolean, format: "pdf" | "png", dedykacja: string) {
  const q = new URLSearchParams({ wariant: prezentowa ? "prezentowa" : "uzytkowa", format });
  if (dedykacja.trim()) q.set("dedykacja", dedykacja.trim());
  return plik(`/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}/etykieta?${q}`);
}

/** Paragon wędzenia (Etap P) - PNG składa Go (zdjęcia przebiegów są w API); ten sam obraz to podgląd i plik. */
export function paragon(sn: string, id: number, u: { uklad: UkladParagonu; pola: ReadonlyArray<PoleParagonu>; kadr: number; zdjecie: boolean }) {
  const q = new URLSearchParams({ uklad: u.uklad, pola: u.pola.join(","), kadr: String(Math.round(u.kadr)), zdjecie: u.zdjecie ? "1" : "0" });
  return plik(`/api/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}/paragon?${q}`);
}

/** Jeden przebieg z fazami (#33) - szczegóły w Statystykach, link z powiadomienia. */
export const przebieg = (sn: string, id: number) =>
  zapytanie<RunReply>("GET", `/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}`);

/** #32 - jeden znacznik próby: true = „Oznacz jako próbę” (znika z listy i statystyk), false = „To było wędzenie”. */
export const ustawProbe = (sn: string, id: number, proba: boolean) =>
  zapytanie<OKReply>("POST", `/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}/proba`, { proba } satisfies RunTrialIn);

/** Zgoda na galerię wędzeń na stronie producenta: true = wniosek do akceptacji, false = poza galerią od razu. */
export const zgodaGalerii = (sn: string, id: number, zgoda: boolean) =>
  zapytanie<RunGalleryReply>("POST", `/brama/urzadzenia/${encodeURIComponent(sn)}/przebiegi/${id}/galeria`, { zgoda } satisfies RunGalleryIn);
